<template>
  <div class="historical-import">
    <b-alert v-if="!featureEnabled" show variant="info">
      <i class="fa fa-info-circle mr-1"></i>
      <strong>Backend support pending.</strong>
      Historical import requires the <code>ManualReading</code> GraphQL type and the
      <code>/api/manual-readings/import/*</code> REST endpoints on <code>leafsvc</code>.
      Set <code>VUE_APP_MANUAL_READINGS_ENABLED=1</code> and rebuild once the API is deployed.
    </b-alert>

    <div v-if="featureEnabled" class="row">
      <div class="col-12">

        <!-- Upload Card -->
        <div class="card mb-3">
          <div class="card-header">
            <h4 class="card-title mb-0">
              <i class="fas fa-file-import mr-2"></i>
              Historical Data Import
            </h4>
          </div>
          <div class="card-body">
            <p class="text-muted small mb-3">
              Backfill historical readings for a single device from a CSV or Excel file. Each row should be a single
              measurement: <code>timestamp, metricName, value, unit (optional), notes (optional)</code>.
              <a href="/sample-historical-import.csv" download>Download sample CSV</a>.
            </p>

            <b-row>
              <b-col md="6">
                <b-form-group label="Target Device *" label-for="hi-device">
                  <b-form-select
                    id="hi-device"
                    v-model="form.deviceId"
                    :options="deviceOptions"
                    :disabled="uploading || preview"
                  />
                </b-form-group>
              </b-col>
              <b-col md="6">
                <b-form-group label="CSV / Excel File *" label-for="hi-file">
                  <b-form-file
                    id="hi-file"
                    v-model="form.file"
                    accept=".csv,.xlsx,.xls"
                    :state="form.file ? true : null"
                    :disabled="uploading || preview"
                    placeholder="Choose a file..."
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <div class="text-right">
              <b-button
                v-if="!preview"
                variant="primary"
                :disabled="!canUpload || uploading"
                @click="uploadAndPreview"
              >
                <b-spinner v-if="uploading" small class="mr-1"></b-spinner>
                <i v-else class="fa fa-upload mr-1"></i>
                {{ uploading ? 'Uploading...' : 'Upload &amp; Preview' }}
              </b-button>
              <b-button v-else variant="outline-secondary" @click="resetForm">
                <i class="fa fa-times mr-1"></i> Start Over
              </b-button>
            </div>
          </div>
        </div>

        <!-- Preview Card -->
        <div v-if="preview" class="card mb-3">
          <div class="card-header">
            <h5 class="mb-0"><i class="fas fa-table mr-2"></i> Preview</h5>
          </div>
          <div class="card-body">

            <b-row class="mb-3">
              <b-col cols="auto">
                <b-badge variant="secondary" class="mr-2">{{ preview.rowCount }} total</b-badge>
                <b-badge variant="success" class="mr-2">{{ preview.summary.ok }} OK</b-badge>
                <b-badge variant="warning" class="mr-2">{{ preview.summary.duplicate }} duplicates</b-badge>
                <b-badge variant="danger">{{ preview.summary.error }} errors</b-badge>
              </b-col>
            </b-row>

            <b-form-group label="Duplicate Handling">
              <b-form-radio-group v-model="form.duplicateStrategy" :options="duplicateOptions" stacked />
            </b-form-group>

            <b-table
              :items="preview.rows"
              :fields="previewFields"
              striped
              small
              responsive
              :per-page="20"
              :current-page="previewPage"
            >
              <template #cell(status)="data">
                <b-badge :variant="statusVariant(data.item.status)">{{ data.item.status }}</b-badge>
              </template>
              <template #cell(error)="data">
                <span v-if="data.item.error" class="text-danger small">{{ data.item.error }}</span>
                <span v-else class="text-muted small">—</span>
              </template>
            </b-table>

            <b-pagination
              v-if="preview.rows.length > 20"
              v-model="previewPage"
              :total-rows="preview.rows.length"
              :per-page="20"
              align="center"
            />

            <div class="text-right mt-3">
              <b-button variant="outline-secondary" class="mr-2" @click="resetForm">Cancel</b-button>
              <b-button
                variant="primary"
                :disabled="committing || preview.summary.ok === 0"
                @click="commitImport"
              >
                <i class="fa fa-check mr-1"></i> Commit Import
              </b-button>
            </div>
          </div>
        </div>

        <!-- Batch History Card -->
        <div class="card">
          <div class="card-header">
            <h5 class="mb-0"><i class="fas fa-history mr-2"></i> Past Imports</h5>
          </div>
          <div class="card-body">
            <div v-if="$apollo.queries.manualReadingBatches.loading" class="text-center py-3">
              <b-spinner small></b-spinner>
            </div>
            <div v-else-if="manualReadingBatches && manualReadingBatches.length" class="table-responsive">
              <table class="table table-striped table-hover">
                <thead>
                  <tr>
                    <th>Batch</th>
                    <th>Device</th>
                    <th class="d-none d-md-table-cell">Filename</th>
                    <th>Rows</th>
                    <th class="d-none d-lg-table-cell">Date Range</th>
                    <th>Imported</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="b in manualReadingBatches" :key="b.batchId">
                    <td><code>{{ b.batchId.substr(0, 8) }}</code></td>
                    <td>{{ b.device ? b.device.name : '—' }}</td>
                    <td class="d-none d-md-table-cell">{{ b.filename || '—' }}</td>
                    <td>{{ b.count }}</td>
                    <td class="d-none d-lg-table-cell small text-muted">
                      {{ formatDate(b.dateRangeStart) }} — {{ formatDate(b.dateRangeEnd) }}
                    </td>
                    <td class="small text-muted">
                      {{ formatTime(b.importedAt) }}<br>
                      <span v-if="b.importedBy">by {{ b.importedBy.firstName }} {{ b.importedBy.lastName }}</span>
                    </td>
                    <td class="text-right">
                      <b-button size="sm" variant="outline-danger" @click="confirmRevert(b)">
                        <i class="fa fa-undo mr-1"></i> Revert
                      </b-button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center text-muted py-3">
              No past imports.
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Commit Progress Modal -->
    <b-modal
      v-model="progressModal.show"
      title="Importing"
      :no-close-on-backdrop="!progressModal.done"
      :no-close-on-esc="!progressModal.done"
      hide-header-close
      centered
      size="sm"
    >
      <div class="text-center py-2">
        <template v-if="!progressModal.done">
          <b-spinner variant="primary" style="width:3rem;height:3rem;" class="mb-3"></b-spinner>
          <p class="mb-3">{{ progressModal.stage }}</p>
          <b-progress :value="progressModal.value" :max="100" animated striped height="8px"></b-progress>
        </template>
        <template v-else-if="progressModal.variant === 'success'">
          <i class="fa fa-check-circle fa-3x text-success mb-3"></i>
          <p class="mb-1 font-weight-bold">Import complete.</p>
          <p class="text-muted small mb-0">{{ progressModal.summary }}</p>
        </template>
        <template v-else>
          <i class="fa fa-exclamation-triangle fa-3x text-warning mb-3"></i>
          <p class="font-weight-bold mb-2">{{ progressModal.stage }}</p>
          <p class="text-muted small mb-0">{{ progressModal.warning }}</p>
        </template>
      </div>
      <template #modal-footer>
        <b-button
          v-if="progressModal.done"
          variant="secondary"
          @click="onProgressClosed"
        >
          Close
        </b-button>
        <span v-else></span>
      </template>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment-timezone'
import {
  ALL_DEVICES_QUERY,
  MANUAL_READING_BATCHES_QUERY,
  DELETE_MANUAL_READING_BATCH_MUTATION
} from '@/constants/graphql'
import { getAccessToken } from '@/lib/auth'
import { MANUAL_READINGS_ENABLED } from '@/constants/features'

const API_BASE = (process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql').replace('/graphql', '')

export default {
  name: 'HistoricalImport',
  data () {
    return {
      form: {
        deviceId: null,
        file: null,
        duplicateStrategy: 'skip'
      },
      uploading: false,
      committing: false,
      preview: null,        // { uploadId, rowCount, rows, summary }
      previewPage: 1,
      progressModal: {
        show: false,
        done: false,
        value: 0,
        stage: '',
        variant: 'primary',
        warning: null,
        summary: null
      },
      duplicateOptions: [
        { value: 'skip', text: 'Skip — leave existing readings unchanged' },
        { value: 'overwrite', text: 'Overwrite — replace existing readings at the same timestamp' },
        { value: 'merge', text: 'Merge — keep both, treating each as a separate entry' }
      ],
      previewFields: [
        { key: 'index', label: '#' },
        { key: 'timestamp', label: 'Timestamp' },
        { key: 'metricName', label: 'Metric' },
        { key: 'value', label: 'Value' },
        { key: 'unit', label: 'Unit' },
        { key: 'status', label: 'Status' },
        { key: 'error', label: 'Notes' }
      ]
    }
  },
  apollo: {
    devices: { query: ALL_DEVICES_QUERY },
    manualReadingBatches: {
      query: MANUAL_READING_BATCHES_QUERY,
      variables () { return {} },
      skip () { return !MANUAL_READINGS_ENABLED },
      fetchPolicy: 'cache-and-network'
    }
  },
  computed: {
    featureEnabled () { return MANUAL_READINGS_ENABLED },
    deviceOptions () {
      const base = [{ value: null, text: 'Select a device...' }]
      if (!this.devices) return base
      return base.concat(this.devices.map(d => ({
        value: d.id,
        text: `${d.name} (${d.serial})`
      })))
    },
    canUpload () {
      return !!(this.form.deviceId && this.form.file)
    }
  },
  methods: {
    formatTime (ts) {
      if (!ts) return '—'
      return moment(ts).format('YYYY-MM-DD HH:mm')
    },
    formatDate (ts) {
      if (!ts) return '—'
      return moment(ts).format('YYYY-MM-DD')
    },
    statusVariant (status) {
      if (status === 'ok') return 'success'
      if (status === 'duplicate') return 'warning'
      if (status === 'error') return 'danger'
      return 'secondary'
    },
    async restFetch (method, path, body, isMultipart) {
      const opts = {
        method,
        headers: { 'Authorization': 'Bearer ' + getAccessToken() }
      }
      if (body !== undefined) {
        if (isMultipart) {
          opts.body = body
        } else {
          opts.headers['Content-Type'] = 'application/json'
          opts.body = JSON.stringify(body)
        }
      }
      const res = await fetch(API_BASE + path, opts)
      if (!res.ok) {
        let errMsg = 'HTTP ' + res.status
        try {
          const json = await res.json()
          if (json && json.error) errMsg = json.error
        } catch (e) { /* ignore */ }
        throw new Error(errMsg)
      }
      return res.json()
    },
    async uploadAndPreview () {
      if (!this.canUpload || this.uploading) return
      this.uploading = true
      try {
        const fd = new FormData()
        fd.append('file', this.form.file)
        fd.append('deviceId', this.form.deviceId)
        const result = await this.restFetch('POST', '/api/manual-readings/import/preview', fd, true)
        this.preview = result
        this.previewPage = 1
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Preview upload failed:', err)
        this.$swal.fire({
          icon: 'error',
          title: 'Upload failed',
          text: err.message || 'An unexpected error occurred.',
          toast: false,
          position: 'center',
          showConfirmButton: true,
          timer: 0
        })
      } finally {
        this.uploading = false
      }
    },
    async commitImport () {
      if (!this.preview || this.committing) return
      this.committing = true
      this.progressModal = {
        show: true, done: false, value: 5, stage: 'Submitting batch...', variant: 'primary', warning: null, summary: null
      }
      try {
        // Kick off the commit
        const commitPromise = this.restFetch(
          'POST',
          '/api/manual-readings/import/' + this.preview.uploadId + '/commit',
          { duplicateStrategy: this.form.duplicateStrategy },
          false
        )

        // Poll status while the commit runs
        const self = this
        const pollInterval = setInterval(async function () {
          try {
            const status = await self.restFetch('GET', '/api/manual-readings/import/' + self.preview.uploadId + '/status', undefined, false)
            if (status && typeof status.progress === 'number') {
              self.progressModal.value = Math.max(self.progressModal.value, Math.min(99, status.progress))
            }
            if (status && status.stage) self.progressModal.stage = status.stage
          } catch (e) { /* ignore poll errors */ }
        }, 1000)

        let result
        try {
          result = await commitPromise
        } finally {
          clearInterval(pollInterval)
        }

        this.progressModal.value = 100
        this.progressModal.done = true
        this.progressModal.variant = 'success'
        this.progressModal.summary =
          `${result.inserted || 0} inserted, ${result.skipped || 0} skipped, ${result.overwritten || 0} overwritten.`
        this.$apollo.queries.manualReadingBatches.refetch()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Commit failed:', err)
        this.progressModal.done = true
        this.progressModal.variant = 'warning'
        this.progressModal.stage = 'Import failed.'
        this.progressModal.warning = err.message || 'An unexpected error occurred.'
      } finally {
        this.committing = false
      }
    },
    onProgressClosed () {
      const wasSuccess = this.progressModal.variant === 'success'
      this.progressModal.show = false
      if (wasSuccess) {
        this.resetForm()
      }
    },
    resetForm () {
      this.form = { deviceId: null, file: null, duplicateStrategy: 'skip' }
      this.preview = null
      this.previewPage = 1
    },
    confirmRevert (batch) {
      this.$swal.fire({
        title: 'Revert this import?',
        text: `Delete all ${batch.count} readings from batch ${batch.batchId.substr(0, 8)}? This cannot be undone.`,
        icon: 'warning',
        toast: false,
        position: 'center',
        showCancelButton: true,
        showConfirmButton: true,
        timer: 0,
        confirmButtonText: 'Revert',
        confirmButtonColor: '#dc3545'
      }).then(result => {
        if (!result.isConfirmed) return
        this.$apollo.mutate({
          mutation: DELETE_MANUAL_READING_BATCH_MUTATION,
          variables: { batchId: batch.batchId }
        }).then(res => {
          const count = (res.data && res.data.deleteManualReadingBatch && res.data.deleteManualReadingBatch.count) || batch.count
          this.$swal.fire({ icon: 'success', title: 'Batch reverted.', text: count + ' readings removed.' })
          this.$apollo.queries.manualReadingBatches.refetch()
        }).catch(err => {
          // eslint-disable-next-line no-console
          console.error('Batch revert failed:', err)
          this.$swal.fire({
            icon: 'error',
            title: 'Failed to revert batch',
            text: err.message || 'An unexpected error occurred.',
            toast: false,
            position: 'center',
            showConfirmButton: true,
            timer: 0
          })
        })
      })
    }
  }
}
</script>

<style scoped>
.historical-import .card-title {
  font-size: 1.1rem;
}
</style>
